import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useApi } from "@/lib/useApi";
import { ImageUpload } from "@/components/admin/ImageUpload";
import { FeaturedBanner } from "@/components/FeaturedBanner";
import { Field, PageTitle, Toggle, btnPrimary, card, inputCls, selectCls } from "@/components/admin/ui";

const OPTIONS = [
  { value: "image", label: "Image banner" },
  { value: "video", label: "Featured video" },
];

export default function AdminDesign() {
  const { data: settings, setData } = useApi("/admin/settings");
  const { data: videos } = useApi("/admin/videos");
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (settings && !form)
      setForm({
        featured_enabled: !!settings.featured_enabled,
        featured_type: settings.featured_type === "none" ? "image" : settings.featured_type,
        featured_video_id: settings.featured_video_id || "",
        featured_image_url: settings.featured_image_url,
        featured_link_url: settings.featured_link_url || "",
      });
  }, [settings, form]);

  if (!form) return <p className="text-[14px] text-[#6B7280]">Loading…</p>;

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/settings", { ...form, featured_video_id: form.featured_video_id || null });
      setData(data);
      toast.success("Design saved");
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  const video = videos?.find((v) => v.id === form.featured_video_id);
  const preview = !form.featured_enabled
    ? null
    : form.featured_type === "video" && video
      ? { type: "video", video, link: `/watch/${video.id}` }
      : form.featured_type === "image" && form.featured_image_url
        ? { type: "image", image_url: form.featured_image_url, link: form.featured_link_url }
        : { type: "placeholder" };

  return (
    <div data-testid="admin-design-page">
      <PageTitle title="Design">
        <button type="button" className={btnPrimary} onClick={save} disabled={saving} data-testid="save-design-button">
          {saving ? "Saving…" : "Save changes"}
        </button>
      </PageTitle>
      <p className="text-[13px] text-[#6B7280] -mt-3 mb-5">How the public site looks.</p>

      <div className={`${card} p-5`}>
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4 sm:gap-6">
          <div>
            <p className="text-[15px] font-semibold">Home Page Header</p>
            <p className="text-[12px] text-[#6B7280]">The wide banner at the very top of the home page. Turn it off to hide it completely.</p>
          </div>
          <Toggle checked={form.featured_enabled} onChange={(v) => setForm({ ...form, featured_enabled: v })} testId="home-header-toggle" />
        </div>

        {form.featured_enabled && (
          <div className="mt-6 space-y-5 border-t border-[#E5E7EB] pt-5">
            <Field label="Header type">
              <div className="space-y-2 mt-1">
                {OPTIONS.map((o) => (
                  <label key={o.value} className="flex items-center gap-2 text-[14px] cursor-pointer">
                    <input
                      type="radio"
                      name="featured_type"
                      checked={form.featured_type === o.value}
                      onChange={() => setForm({ ...form, featured_type: o.value })}
                      className="accent-[#B644DC]"
                      data-testid={`featured-type-${o.value}`}
                    />
                    {o.label}
                  </label>
                ))}
              </div>
            </Field>

            {form.featured_type === "video" && (
              <Field label="Video" hint="The header shows the video thumbnail and links to its watch page.">
                <select value={form.featured_video_id} onChange={(e) => setForm({ ...form, featured_video_id: e.target.value })} className={selectCls} data-testid="featured-video-select">
                  <option value="">Choose a video…</option>
                  {videos?.filter((v) => v.visibility !== "private").map((v) => (
                    <option key={v.id} value={v.id}>{v.title}</option>
                  ))}
                </select>
              </Field>
            )}

            {form.featured_type === "image" && (
              <>
                <div>
                  <p className="text-[13px] font-semibold text-[#111] mb-1.5">Header image</p>
                  <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-5">
                    <ImageUpload
                      value={form.featured_image_url}
                      onChange={(url) => setForm({ ...form, featured_image_url: url })}
                      aspect="1010 / 120"
                      hint="Click to upload image"
                      className="w-full sm:w-[420px] shrink-0"
                      testId="featured-image-upload"
                    />
                    <div className="text-[12px] text-[#6B7280] leading-[1.7] pt-0.5" data-testid="home-header-size-hint">
                      <p className="font-semibold text-[#111]">Recommended size</p>
                      <p>2244 × 268 px (8.4:1 wide banner)</p>
                      <p>JPG, PNG or WebP · under 2 MB</p>
                    </div>
                  </div>
                </div>
                <Field label="Link (optional)">
                  <input value={form.featured_link_url} onChange={(e) => setForm({ ...form, featured_link_url: e.target.value })} placeholder="https://…" className={inputCls} data-testid="featured-link-input" />
                </Field>
              </>
            )}
          </div>
        )}

        <div className="mt-6 border-t border-[#E5E7EB] pt-5">
          <p className="text-[13px] font-semibold mb-1">Preview</p>
          {preview ? (
            <div className="bg-[#F7F8F5] rounded-lg px-6 pb-6 -mt-2" data-testid="home-header-preview">
              <FeaturedBanner featured={preview} />
            </div>
          ) : (
            <p className="text-[12px] text-[#6B7280]" data-testid="home-header-preview-off">
              The header is off — the home page starts straight at the first row of videos.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
